import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { getCrossID, getMappedFilters } from "../../utils/filters";
import { useWeb5Context } from "../../lib/Web5Context";
import { getSelectedFilters } from "./utils";
import { isEqual } from "../../utils/objects";
const useAddModal = _ref => {
  let {
    entity,
    defaultFilters = [],
    extraSchema,
    extraMultiSchema,
    onConfirmAdd,
    onDeleteMultiple,
    onResetVisibles,
    visibleFilters,
    selectorsSchema,
    labels = {},
    onDelete,
    forceSelectAllTypes
  } = _ref;
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isOpenAddModal, setIsOpenAddModal] = useState(false);
  const oldSelectedFilters = useRef({});
  const tempVisibleFilters = useRef([]);
  const innerVisibleFilters = useRef([]);
  const initialSelectedFilters = useRef({});
  const isFirstLoad = useRef(true);
  const prevIsOpen = useRef(false);
  const {
    t
  } = useWeb5Context();
  const handleDeleteFilter = useCallback(filter => {
    oldSelectedFilters.current = selectedFilters;
    onDelete && onDelete(filter);
  }, [oldSelectedFilters, onDelete, selectedFilters]);
  const onToggleAddModal = useCallback(() => {
    setIsOpenAddModal(!isOpenAddModal);
  }, [isOpenAddModal]);
  const onChangeSelectedFilters = useCallback(newSelected => {
    let finalSelected = getSelectedFilters(newSelected, entity);
    tempVisibleFilters.current = finalSelected;
    setSelectedFilters(_objectSpread({}, newSelected));
  }, [setSelectedFilters, entity]);
  const handleCancelAdd = useCallback(() => {
    onChangeSelectedFilters(initialSelectedFilters.current);
    tempVisibleFilters.current = [];
  }, [onChangeSelectedFilters]);
  useEffect(() => {
    // Every time we open the modal we define the initialSelectedFilters, to have the
    // object available is we cancel and need to reset to the same state it was when opening
    if (isOpenAddModal && prevIsOpen.current !== isOpenAddModal) {
      prevIsOpen.current = true;
      initialSelectedFilters.current = JSON.parse(JSON.stringify(selectedFilters));
      oldSelectedFilters.current = JSON.parse(JSON.stringify(selectedFilters));
    } else if (!isOpenAddModal) {
      prevIsOpen.current = false;
    }
  }, [selectedFilters, isOpenAddModal]);
  const getUnselectedFilters = useCallback(() => {
    const innerGetUnselectedFilters = (oldFilters, newFilters) => {
      let oldSelected = [];
      let selected = [];
      if (Object.keys(oldFilters).length > 0) {
        oldSelected = Object.keys(oldFilters).reduce((arr, key) => {
          var _oldFilters$key, _oldFilters$key$optio;
          if (((_oldFilters$key = oldFilters[key]) === null || _oldFilters$key === void 0 ? void 0 : (_oldFilters$key$optio = _oldFilters$key.options) === null || _oldFilters$key$optio === void 0 ? void 0 : _oldFilters$key$optio.length) > 0) {
            var _oldFilters$key2;
            arr = [...arr, ...(((_oldFilters$key2 = oldFilters[key]) === null || _oldFilters$key2 === void 0 ? void 0 : _oldFilters$key2.options) || [])];
          }
          return arr;
        }, []);
      }
      if (Object.keys(newFilters).length > 0) {
        selected = Object.keys(newFilters).reduce((arr, key) => {
          var _newFilters$key, _newFilters$key$optio;
          if (((_newFilters$key = newFilters[key]) === null || _newFilters$key === void 0 ? void 0 : (_newFilters$key$optio = _newFilters$key.options) === null || _newFilters$key$optio === void 0 ? void 0 : _newFilters$key$optio.length) > 0) {
            var _newFilters$key2;
            arr = [...arr, ...(((_newFilters$key2 = newFilters[key]) === null || _newFilters$key2 === void 0 ? void 0 : _newFilters$key2.options) || [])];
          }
          return arr;
        }, []);
      }
      const unselectedFilters = oldSelected.reduce((arr, current) => {
        if (selected.length === 0 || !selected.includes(current)) {
          arr.push(current);
        }
        return arr;
      }, []);
      return unselectedFilters;
    };
    if (extraMultiSchema !== null && extraMultiSchema !== void 0 && extraMultiSchema.length) {
      return extraMultiSchema.reduce((obj, schema) => {
        var _oldSelectedFilters$c, _oldSelectedFilters$c2, _selectedFilters$id;
        const {
          id
        } = schema;
        const unselectedFilters = innerGetUnselectedFilters(((_oldSelectedFilters$c = oldSelectedFilters.current) === null || _oldSelectedFilters$c === void 0 ? void 0 : (_oldSelectedFilters$c2 = _oldSelectedFilters$c[id]) === null || _oldSelectedFilters$c2 === void 0 ? void 0 : _oldSelectedFilters$c2.options) || {}, (selectedFilters === null || selectedFilters === void 0 ? void 0 : (_selectedFilters$id = selectedFilters[id]) === null || _selectedFilters$id === void 0 ? void 0 : _selectedFilters$id.options) || {});
        if (unselectedFilters.length > 0) obj[id] = unselectedFilters;
        return obj;
      }, {});
    } else {
      return innerGetUnselectedFilters(oldSelectedFilters.current, selectedFilters);
    }
  }, [extraMultiSchema, selectedFilters]);
  const handleConfirmAdd = useCallback(selected => {
    let newAddedFields = [];
    const unselectedFilters = getUnselectedFilters();
    const innerHandleConfirmAdd = (selectedObj, id) => {
      return selectedObj && Object.keys(selectedObj).reduce((arr, key) => {
        let options = selectedObj[key].options || [];
        if (id && id !== entity.entity) options = options.map(option => getCrossID(option, id));
        arr = [...arr, ...options];
        return arr;
      }, []) || [];
    };
    if (extraMultiSchema !== null && extraMultiSchema !== void 0 && extraMultiSchema.length) {
      if (Object.entries(unselectedFilters).length > 0) onDeleteMultiple(unselectedFilters);
      newAddedFields = extraMultiSchema.reduce((arr, schema) => {
        const {
          id
        } = schema;
        return selected[id] ? [...arr, ...innerHandleConfirmAdd(selected[id].options, id)] : arr;
      }, []);
    } else {
      if ((unselectedFilters === null || unselectedFilters === void 0 ? void 0 : unselectedFilters.length) > 0) onDeleteMultiple(unselectedFilters);
      newAddedFields = innerHandleConfirmAdd(selected);
    }
    oldSelectedFilters.current = JSON.parse(JSON.stringify(selected));
    innerVisibleFilters.current = [...(tempVisibleFilters.current || [])];
    tempVisibleFilters.current = [];
    onConfirmAdd && onConfirmAdd(newAddedFields);
    setIsOpenAddModal(false);
  }, [getUnselectedFilters, extraMultiSchema, onConfirmAdd, entity === null || entity === void 0 ? void 0 : entity.entity, onDeleteMultiple]);
  const getInitialSelected = useCallback(_ref2 => {
    let {
      schema,
      visibleFilters,
      multiSchema,
      isReset = false
    } = _ref2;
    let newInitialSelected = {};
    const innerGetInitialSelected = function (currentSchema, currentVisible) {
      if (currentVisible === void 0) {
        currentVisible = [];
      }
      return currentSchema.reduce((obj, group) => {
        let options = [];
        if (group !== null && group !== void 0 && group.options) {
          options = group.options.reduce((arr, option) => {
            if (currentVisible.includes(option.id)) {
              arr.push(option.id);
            }
            return arr;
          }, []);
        }
        obj[group.id] = {
          isSelected: options.length > 0 && options.length === group.options.length || false,
          options
        };
        return obj;
      }, {});
    };
    if (multiSchema !== null && multiSchema !== void 0 && multiSchema.length) {
      if (!(multiSchema !== null && multiSchema !== void 0 && multiSchema.length) > 0) return {};
      const mappedVisibleFilters = getMappedFilters(visibleFilters, entity.entity);
      newInitialSelected = multiSchema.reduce((obj, currentSchema) => {
        const {
          id,
          schema
        } = currentSchema;
        const options = innerGetInitialSelected(schema, mappedVisibleFilters === null || mappedVisibleFilters === void 0 ? void 0 : mappedVisibleFilters[id]);
        const isSelected = isReset && forceSelectAllTypes ? true : Object.values(options).some(option => option.isSelected || option.options.length > 0);
        obj[id] = {
          isExpanded: true,
          isSelected,
          // In the case of entities with cross filters we want to keep the filters of the main entity
          // always visible in the filters selection modal so we override the isSelected value with overrideIsSelected
          overrideIsSelected: id === entity.entity,
          options
        };
        return obj;
      }, {});
    } else {
      if (!(schema !== null && schema !== void 0 && schema.length) > 0) return {};
      newInitialSelected = innerGetInitialSelected(schema, visibleFilters);
    }
    onChangeSelectedFilters(_objectSpread({}, newInitialSelected));
  }, [entity === null || entity === void 0 ? void 0 : entity.entity, onChangeSelectedFilters, forceSelectAllTypes]);
  const onResetSelected = useCallback(initialFilters => {
    onChangeSelectedFilters(initialFilters);
    getInitialSelected({
      schema: extraSchema,
      visibleFilters: defaultFilters,
      multiSchema: extraMultiSchema,
      isReset: true
    });
    tempVisibleFilters.current = [];
    innerVisibleFilters.current = defaultFilters;
    onResetVisibles && onResetVisibles();
  }, [defaultFilters, onChangeSelectedFilters, onResetVisibles, extraSchema, extraMultiSchema, getInitialSelected]);
  useEffect(() => {
    if (isFirstLoad.current) {
      getInitialSelected({
        schema: extraSchema,
        visibleFilters,
        multiSchema: extraMultiSchema
      });
      isFirstLoad.current = false;
    }
  }, [getInitialSelected, extraSchema, visibleFilters, extraMultiSchema, defaultFilters]);

  // When is the first load, sometimes the visibleFilters are not yet loaded, so we need to control that we load them when they are.
  // That's why we use tempInnerVisibleFilters and innerVisibleFilters refs to have an internal control.
  useEffect(() => {
    if (!isFirstLoad.current && !isEqual(visibleFilters, innerVisibleFilters.current)) {
      getInitialSelected({
        schema: extraSchema,
        visibleFilters,
        multiSchema: extraMultiSchema
      });
    }
  }, [extraMultiSchema, extraSchema, visibleFilters, getInitialSelected]);
  const modalOptionsProps = useMemo(() => {
    let baseProps = {
      title: labels.addExtraField || t('action_add_extra_field'),
      confirmText: (labels === null || labels === void 0 ? void 0 : labels.confirm) || t('action_accept'),
      cancelText: (labels === null || labels === void 0 ? void 0 : labels.cancel) || t('action_cancel'),
      deleteText: (labels === null || labels === void 0 ? void 0 : labels.reset) || t('action_reset_filter_fields_selection'),
      placeholderSearch: (labels === null || labels === void 0 ? void 0 : labels.searchField) || t('label_search_field'),
      onResetSelected,
      defaultFilters,
      forceSelectAllTypes: forceSelectAllTypes || false,
      isOpen: isOpenAddModal,
      onToggleModal: onToggleAddModal,
      onConfirm: handleConfirmAdd,
      onCancel: handleCancelAdd,
      selected: selectedFilters,
      onChangeSelected: onChangeSelectedFilters,
      handleDeleteFilter
    };
    if (extraMultiSchema && selectorsSchema) {
      baseProps.multiSchema = extraMultiSchema;
      baseProps.selectorsSchema = selectorsSchema;
    } else {
      baseProps.schema = extraSchema;
    }
    return baseProps;
  }, [defaultFilters, extraMultiSchema, extraSchema, handleCancelAdd, handleConfirmAdd, handleDeleteFilter, isOpenAddModal, labels, onChangeSelectedFilters, onResetSelected, onToggleAddModal, selectedFilters, selectorsSchema, t, forceSelectAllTypes]);
  return modalOptionsProps;
};
export default useAddModal;